import React from 'react';
import { useIntl } from 'react-intl';
import { map } from 'rxjs';
import Html from 'components/html';
import { IconList } from 'components/icon';
import IconHover from 'components/icon-hover';
import Link from 'components/link';
import useReadonlyObservable from 'enhancers/use-readonly-observable';
import application from 'services/application';
import { sendTracking } from 'services/google-analytics';
import { link } from 'services/navigation';

import tracking from './online-support.tracking';
import SupportChat from './support-chat';
import messages from './online-support.i18n';
import { bottomPageScrollOffset, testId } from './online-support.settings';
import styles from './online-support.module.scss';

type ChatState = 'closed' | 'maximized' | 'minimized';

const OnlineSupport: React.FunctionComponent<unknown> = () => {
  const [buttonHover, setButtonHover] = React.useState(false);
  const [chatState, setChatState] = React.useState<ChatState>('closed');
  const [componentEmbedded, setComponentEmbedded] = React.useState(false);
  const { formatMessage } = useIntl();
  const translations = messages.pages.landing.onlineSupport;

  const [pageBottomReached] = useReadonlyObservable(
    application.onScroll$.pipe(
      map((event) => {
        if (!event?.root) return false;

        const { scrollTop, scrollHeight, clientHeight } = event.root;

        return Math.ceil(clientHeight + scrollTop) >= scrollHeight - bottomPageScrollOffset;
      })
    ),
    false
  );

  const handleButtonHover = React.useCallback(
    (hover: boolean) => () => {
      setButtonHover(hover);
    },
    []
  );

  const handleOpenClose = React.useCallback(() => {
    if (chatState === 'closed') {
      sendTracking(tracking.startChat);
      setChatState('maximized');

      return;
    }

    sendTracking(tracking.endChat);
    setChatState('closed');
  }, [chatState]);

  const handleClickMinimize = React.useCallback(() => {
    sendTracking(tracking.minimizeChat);
    setChatState('minimized');
  }, []);

  const handleClickMaximize = React.useCallback(() => {
    sendTracking(tracking.maximizeChat);
    setChatState('maximized');
  }, []);

  const handleOnKicked = React.useCallback(() => {
    sendTracking(tracking.kicked);
    setChatState('closed');
  }, []);

  React.useEffect(() => {
    setComponentEmbedded(chatState !== 'closed');
  }, [chatState]);

  const supportChat = React.useMemo(() => {
    if (!componentEmbedded) return null;

    return (
      <Html.div className={styles.chat}>
        <SupportChat onKicked={handleOnKicked} />
      </Html.div>
    );
  }, [componentEmbedded, handleOnKicked]);

  return (
    <Html.div
      className={[styles.onlineSupport, pageBottomReached && styles.pageBottomReached]}
      testId={testId.component}
    >
      <Html.div className={[styles.chatContainer, styles[chatState]]} testId={`${testId.chatContainer}-${chatState}`}>
        <Html.div className={[styles.title, 'px-4 pt-4 pb-1']} typography="title2" fontWeight="bold">
          {formatMessage(translations.header.title)}
        </Html.div>
        <Html.div className={[styles.description, 'px-4 pb-3']} typography="body2">
          {formatMessage(translations.header.description, {
            a: (value: React.ReactNode) => (
              <Link href={link.faq()} target="_blank" variant="primary">
                {value}
              </Link>
            ),
          })}
        </Html.div>
        <Html.div className={styles.minimizeButton} onClick={handleClickMinimize} testId={testId.minimizeButton}>
          <IconHover active={IconList.minusSolid} inactive={IconList.minusOutline} />
        </Html.div>
        {supportChat}
      </Html.div>
      {chatState === 'minimized' && (
        <Html.div className={[styles.minimizedChat, 'px-2']} testId={testId.minimizedChat}>
          <Html.div className={[styles.title, 'p-0 mb-0 ms-1']} typography="title2" fontWeight="bold">
            {formatMessage(translations.header.title)}
          </Html.div>
          <Html.div className={styles.maximizeButton} onClick={handleClickMaximize} testId={testId.maximizeButton}>
            <IconHover active={IconList.plusSolid} inactive={IconList.plusOutline} />
          </Html.div>
        </Html.div>
      )}
      <Html.button
        className={styles.icon}
        onMouseEnter={handleButtonHover(true)}
        onMouseLeave={handleButtonHover(false)}
        onClick={handleOpenClose}
        testId={testId.toggleIcon}
      >
        {chatState === 'closed' && (
          <IconHover
            forceActive={buttonHover}
            active={IconList.headphonesSolid}
            inactive={IconList.headphonesOutline}
          />
        )}
        {chatState !== 'closed' && (
          <IconHover forceActive={buttonHover} active={IconList.closeSolid} inactive={IconList.closeOutline} />
        )}
      </Html.button>
    </Html.div>
  );
};

export default OnlineSupport;
